/**
 * Font resolution for the branding system.
 *
 * The branding API returns a font name (e.g. "Inter", "AeonikPro", "Playfair Display").
 * This module resolves it to a CSS font-family stack using three strategies:
 *
 *  1. Bundled variable fonts (fontsource) — e.g. "Inter" → "Inter Variable"
 *  2. Bundled proprietary fonts (woff2 in-repo) — e.g. "AeonikPro" → @font-face injection
 *  3. Google Fonts (dynamic <link>) — any font not in the registry is loaded at runtime
 *
 * Fonts in FONT_REGISTRY are preferred. Unrecognised names fall through to Google Fonts,
 * which degrades gracefully — if the name doesn't match a real Google Font, the browser
 * uses the fallback stack (Public Sans → Arial → sans-serif). System fonts (e.g. "Comic
 * Sans MS") also work via the fallback path since they need no loading.
 *
 * Adding a bundled font:
 *  1. Install the fontsource package (or add woff2 files to this directory)
 *  2. Import the CSS / woff2 URLs in this file
 *  3. Add an entry to FONT_REGISTRY (with `faces` array for proprietary woff2 fonts)
 */

import "@fontsource-variable/inter";
import "@fontsource-variable/public-sans";

import AeonikProBold from "./AeonikPro-Bold.woff2?url";
import AeonikProMedium from "./AeonikPro-Medium.woff2?url";
import AeonikProMediumItalic from "./AeonikPro-MediumItalic.woff2?url";
import AeonikProRegular from "./AeonikPro-Regular.woff2?url";
import AeonikProRegularItalic from "./AeonikPro-RegularItalic.woff2?url";

type FontFace = { url: string; weight: number; style: "normal" | "italic" };

type FontRegistryEntry = {
  cssFontFamily: string;
  fontFamilyWithFallbacks: string;
  /** For licensed fonts bundled in-repo: @font-face definitions injected on demand. */
  faces?: FontFace[];
};

// API font name (lowercase) -> registry entry
const FONT_REGISTRY: Record<string, FontRegistryEntry> = {
  "public sans": {
    cssFontFamily: "Public Sans Variable",
    fontFamilyWithFallbacks: "'Public Sans Variable', Arial, sans-serif",
  },
  inter: {
    cssFontFamily: "Inter Variable",
    fontFamilyWithFallbacks: "'Inter Variable', Arial, sans-serif",
  },
  aeonikpro: {
    cssFontFamily: "AeonikPro",
    fontFamilyWithFallbacks: "AeonikPro, Arial, sans-serif",
    faces: [
      { url: AeonikProRegular, weight: 300, style: "normal" },
      { url: AeonikProRegularItalic, weight: 300, style: "italic" },
      { url: AeonikProMedium, weight: 500, style: "normal" },
      { url: AeonikProMediumItalic, weight: 500, style: "italic" },
      { url: AeonikProBold, weight: 700, style: "normal" },
    ],
  },
};

const FALLBACK_KEY = "public sans";
const LOCAL_FONTS_STYLE_ID = "propai-local-fonts";
const GOOGLE_FONTS_LINK_ID = "propai-google-fonts";
const injectedKeys = new Set<string>();

/** Inject @font-face rules for a licensed/bundled font. Idempotent per key. */
function injectLocalFont(key: string, cssFontFamily: string, faces: FontFace[]) {
  if (typeof document === "undefined" || injectedKeys.has(key)) return;

  const styleEl =
    document.getElementById(LOCAL_FONTS_STYLE_ID) ??
    Object.assign(document.createElement("style"), { id: LOCAL_FONTS_STYLE_ID });

  const rules = faces
    .map(
      (f) =>
        `@font-face { font-family: '${cssFontFamily}'; font-style: ${f.style}; font-weight: ${f.weight}; font-display: swap; src: url('${f.url}') format('woff2'); }`
    )
    .join("\n");

  styleEl.textContent = (styleEl.textContent ?? "") + "\n" + rules;
  if (!styleEl.isConnected) document.head.appendChild(styleEl);
  injectedKeys.add(key);
}

/**
 * Load a font dynamically from Google Fonts via a <link> tag.
 * Idempotent — skips if the font was already requested.
 * Falls back gracefully: if the font doesn't exist on Google Fonts,
 * the link 404s silently and the browser uses the CSS fallback stack.
 */
function loadGoogleFont(fontFamily: string) {
  if (typeof document === "undefined" || injectedKeys.has(fontFamily)) return;

  const linkEl =
    document.getElementById(GOOGLE_FONTS_LINK_ID) ??
    Object.assign(document.createElement("link"), {
      id: GOOGLE_FONTS_LINK_ID,
      rel: "stylesheet",
    });

  // Google Fonts API accepts family names with spaces encoded as `+`
  const encoded = fontFamily.replace(/ /g, "+");
  const base = "https://fonts.googleapis.com/css2";
  const currentHref = linkEl.getAttribute("href") ?? "";

  // Append to existing href if we already have families loading
  const newHref = currentHref
    ? `${currentHref}&family=${encoded}:wght@300;400;500;700&display=swap`
    : `${base}?family=${encoded}:wght@300;400;500;700&display=swap`;

  linkEl.setAttribute("href", newHref);
  if (!linkEl.isConnected) document.head.appendChild(linkEl);
  injectedKeys.add(fontFamily);
}

// "Inter, Arial, sans-serif" -> "inter"
function extractPrimaryFont(fontFamily: string): string {
  return fontFamily.split(",")[0].trim().replace(/['"]/g, "").toLowerCase();
}

/**
 * Takes the font name from the branding API, loads the font if needed,
 * and returns the correct font-family stack for CSS.
 *
 * Resolution order:
 *  1. Bundled registry entry (fontsource or local woff2 files)
 *  2. Google Fonts (loaded dynamically via <link>)
 *
 * Google Fonts degrades gracefully — if the name doesn't match a real
 * Google Font the request silently fails and the fallback stack applies.
 */
export function resolveFontFamily(brandingFontFamily: string): string {
  const key = extractPrimaryFont(brandingFontFamily);
  const entry = FONT_REGISTRY[key];

  // Bundled font — use the registry entry directly
  if (entry) {
    if (entry.faces) {
      injectLocalFont(key, entry.cssFontFamily, entry.faces);
    }
    return entry.fontFamilyWithFallbacks;
  }

  // Not in the registry — attempt to load from Google Fonts.
  // Use the original casing from the API for the font-family name.
  const originalName = brandingFontFamily.split(",")[0].trim().replace(/['"]/g, "");
  loadGoogleFont(originalName);
  return `'${originalName}', '${FONT_REGISTRY[FALLBACK_KEY].cssFontFamily}', Arial, sans-serif`;
}

export const SUPPORTED_FONTS = Object.entries(FONT_REGISTRY).map(
  ([, entry]) => entry.cssFontFamily
);
